import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IOrganizationUser, IUser, IUserPermissions } from "./models";
import {
  userService,
} from "./services";
import { useBoundStore } from "store";
import { isEmpty, omit } from "lodash";
import { RcFile } from "antd/es/upload";

export const userKeys = {
  organizationUser: (tenantId?: string, userId?: string, organizationId?: string) =>
    ["users", "tenant", tenantId ?? "unknown", userId ?? "unknown", "organizations", organizationId ?? "unknown"] as const,
  tenantUser: (userId?: string, tenantId?: string) =>
    ["users", userId ?? "unknown", "tenants", tenantId ?? "unknown"] as const,
  myPermissions: (tenantId?: string, organizationId?: string) =>
    ["users", "me", "tenant", tenantId ?? "unknown", "permissions", organizationId ?? "unknown"] as const,
  mentionOptions: (tenantId?: string, organizationId?: string) =>
    ["users", "tenant", tenantId ?? "unknown", "organization", organizationId ?? "unknown", "mention-options"] as const,
};

export function useOrganizationUser(
  userId?: string,
  organizationId?: string,
  options?: { enabled?: boolean }
) {
  const { tenant } = useBoundStore();

  return useQuery({
    queryKey: userKeys.organizationUser(tenant?.id, userId, organizationId),
    queryFn: () =>
      userService.getOrganizationUser(tenant!.id, organizationId as string, userId as string) as Promise<IOrganizationUser>,
    enabled: !!tenant?.id && !!userId && !!organizationId && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useUpdateOrganizationUserMetadata(
  userId?: string,
  organizationId?: string,
) {
  const queryClient = useQueryClient();
  const { tenant } = useBoundStore();

  return useMutation({
    mutationFn: (metadata: Record<string, any>) =>
      userService.updateOrganizationUserMetadata(tenant!.id, organizationId as string, userId as string, metadata),
    onSuccess: (data) => {
      // Update the query cache with the new data
      queryClient.setQueryData(userKeys.organizationUser(tenant?.id, userId, organizationId), data);
    },
  });
}

export function useTenantUser(
  userId?: string,
  tenantId?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: userKeys.tenantUser(userId, tenantId),
    queryFn: () => userService.getTenantUser(userId as string, tenantId as string),
    enabled: !!userId && !!tenantId && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/**
 * Fetches the current user's permissions for a specific organization.
 * Permissions are fetched from the role_permissions database table.
 */
export function useMyPermissions(
  organizationId?: string,
  options?: { enabled?: boolean }
) {
  const { tenant, isAxiosInterceptorsReady } = useBoundStore();

  return useQuery({
    queryKey: userKeys.myPermissions(tenant?.id, organizationId),
    queryFn: () => userService.getMyPermissions(tenant!.id, organizationId as string) as Promise<IUserPermissions>,
    enabled: !!tenant?.id && !!organizationId && isAxiosInterceptorsReady && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
  });
}

/**
 * Fetches user mention options for an organization (used in @-mention dropdowns).
 */
export function useUserMentionOptions(
  organizationId?: string,
  options?: { enabled?: boolean }
) {
  const { tenant } = useBoundStore();

  return useQuery({
    queryKey: userKeys.mentionOptions(tenant?.id, organizationId),
    queryFn: () => userService.getUserMentionOptions(tenant!.id, organizationId as string),
    enabled: !!tenant?.id && !!organizationId && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/**
 * Mutation to update the current user's profile (firstName, lastName, email).
 * Uses the tenant-scoped endpoint and invalidates the tenantUser cache on success.
 */
export function useUpdateTenantUserMutation() {
  const queryClient = useQueryClient();
  const { user, tenant } = useBoundStore();

  return useMutation({
    mutationFn: (profile: { firstName?: string; lastName?: string; email?: string; phoneNumber?: string; company?: string; service?: string }) => {
      if (!user?.auth0UserId) throw new Error("User not authenticated.");
      if (!tenant?.id) throw new Error("Tenant not found.");
      // Self-service endpoint (update:profile) so non-admins can edit their own info.
      return userService.updateOwnProfile(user.auth0UserId, tenant.id, profile);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userKeys.tenantUser(user?.auth0UserId, tenant?.id),
      });
    },
  });
}

/**
 * Mutation to update a user profile.
 * If no id is provided, updates the current user and syncs the store.
 * If an id is provided, fetches the full user first, merges the updates, then saves.
 */
export function useUpdateUserMutation() {
  const queryClient = useQueryClient();
  const { setUser } = useBoundStore();
  const currentUser = useBoundStore((state) => state.user);

  return useMutation({
    mutationFn: async ({
      userToUpdate,
      id,
      file,
    }: {
      userToUpdate: Partial<IUser>;
      id?: string;
      file?: RcFile;
    }) => {
      let idToUpdate = id;
      let merged = { ...userToUpdate };
      let propertiesToOmit = ["roleId", "uid", "profilePicture", "id", "gsi"];

      if (!id && !currentUser?.id) {
        throw new Error("User id is required.");
      }

      if (!id) {
        idToUpdate = currentUser.id;
        merged = { ...currentUser, ...userToUpdate };
      } else {
        const fullUser = await userService.getUser(idToUpdate as string);
        merged = { ...fullUser, ...userToUpdate };
      }

      if (isEmpty(merged?.role)) {
        propertiesToOmit.push("role");
      }

      const user = await userService.updateUser(
        idToUpdate as string,
        omit(merged, propertiesToOmit) as IUser,
        file,
        !id
      );

      return { user, isCurrentUser: !id };
    },
    onSuccess: ({ user, isCurrentUser }) => {
      if (isCurrentUser) {
        setUser(user);
      }
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}
